import { PopoverPosition, Text } from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { v4 as uuid } from 'uuid';

import { ItemSelector } from 'bones/components/molecules/ItemSelector/ItemSelector';
import { useModelClassItems } from 'bones/components/molecules/QueryBuilder/hooks/selectorItems';
import { useQueryBuilderContext } from 'bones/components/molecules/QueryBuilder/hooks/useQueryBuilder';
import {
  useOnQueryClasses,
  useQueryBuilderModelContext,
} from 'bones/components/molecules/QueryBuilder/hooks/useQueryBuilderModel';
import { useQueryBuilderSchemaContext } from 'bones/components/molecules/QueryBuilder/hooks/useQueryBuilderSchema';
import { RDF_TYPE_IRI } from 'bones/constants/iris';
import { getIntentClassName } from 'bones/utils/getIntentClassName';
import { getLocalName } from 'bones/utils/getLocalName';
import {
  ClassSelectItem,
  ClassSubSelectItem,
  createClassSelectItem,
} from 'bones/utils/queryBuilder/helpers/select';
import { DraftObjectOperator, Variable } from 'bones/utils/queryBuilder/types';
import { SelectItem } from 'bones/utils/select/types';

export interface ClassSelectorProps {
  invalid?: boolean;
  items: ClassSelectItem[];
  operator?: DraftObjectOperator;
  selected: Variable | null;
  testId: string;
  tooltip: string;
  useVariableLabel?: boolean;
  onSelect: (item: ClassSubSelectItem) => void;
}

const findItemsForVariable = (
  selected: Variable,
  items: ClassSelectItem[]
): { item: ClassSelectItem | null; subItem: ClassSubSelectItem | null } => {
  let foundSubItem: SelectItem<ClassSubSelectItem> | null = null;

  const foundItem =
    items.find((item: ClassSelectItem) => {
      if (item.items?.length) {
        const subItem = item.items.find((sub) => sub.value === selected.id);
        if (subItem) {
          foundSubItem = subItem;
          return true;
        }
      }
      return item.value === selected.iri;
    }) || null;

  return { item: foundItem, subItem: foundSubItem };
};

export const ClassSelector: React.VFC<ClassSelectorProps> = ({
  invalid,
  items,
  operator,
  selected,
  testId,
  tooltip,
  useVariableLabel = true,
  onSelect,
}) => {
  const { copy, testIds, readOnly, creatableClassProps } =
    useQueryBuilderSchemaContext();
  const { isLimited, isLoadingClasses } = useQueryBuilderModelContext();
  const { getVariableLabel } = useQueryBuilderContext();
  const allClassItems = useModelClassItems();

  const { item, subItem } = React.useMemo((): {
    item: ClassSelectItem | null;
    subItem: ClassSubSelectItem | null;
  } => {
    if (!selected) {
      return { item: null, subItem: null };
    }

    const fromItems = findItemsForVariable(selected, items);
    if (fromItems.item) {
      return fromItems;
    }

    const foundAllItems = findItemsForVariable(selected, allClassItems);
    if (foundAllItems.item) {
      return foundAllItems;
    }

    return {
      item: createClassSelectItem({
        iri: selected.iri,
        label: selected.newLabel || getLocalName(selected.iri),
        colorId: 'sd-qb-black',
      }),
      subItem: null,
    };
  }, [allClassItems, items, selected]);

  const handleSelect = (item: ClassSelectItem | ClassSubSelectItem | null) => {
    // istanbul ignore else
    if (item && item.iri) {
      onSelect(item);
    } else if (item && item.isNewlyCreated) {
      onSelect({
        iri: item.value,
        text: item.text,
        value: uuid(),
        isNewlyCreated: true,
      });
    }
  };

  let buttonText: string | React.ReactElement = copy.body.class.placeholder;
  if (selected && item) {
    const { label, num } = getVariableLabel(item.text, selected);
    buttonText =
      useVariableLabel && num !== null ? (
        <div className="sd-query-builder-class-selector-button-text">
          <Text ellipsize>{label}</Text>
          <Text>&nbsp;{`(${num})`}</Text>
        </div>
      ) : (
        label
      );
  }

  const [wasOpened, setWasOpened] = React.useState(false);
  const [queryKey, setQueryKey] = React.useState('');
  const debouncedSetQueryKey = useDebouncedCallback(setQueryKey, 500, {
    leading: true,
  });
  useOnQueryClasses({
    disabled: !wasOpened,
    query: queryKey,
    propertyIri: operator?.iri,
  });

  return (
    <ItemSelector.Single<ClassSubSelectItem, ClassSelectItem>
      buttonClassName={classNames({
        [getIntentClassName(item?.colorId || '')]: item?.colorId,
      })}
      buttonOutlined={Boolean(item && item.colorId === 'sd-qb-black')}
      buttonRightIcon={selected ? null : undefined}
      buttonText={buttonText}
      className={classNames('sd-query-builder-class-selector', {
        'sd-query-builder-class-selector--invalid': invalid,
        'sd-query-builder-class-selector--read-only': readOnly,
      })}
      creatable={creatableClassProps && operator?.iri === RDF_TYPE_IRI}
      createNewItemFromQuery={creatableClassProps?.createNewItemFromQuery}
      createNewItemRenderer={creatableClassProps?.createNewItemRenderer}
      createNewItemTestId={testIds.classSelectorItem('')}
      createText={copy.body.filter.createText}
      disabled={readOnly}
      filterable
      filterOrCreateText={copy.body.filter.createPlaceholder}
      filterTestId={testIds.classSelectorFilter}
      filterText={
        isLimited
          ? copy.body.filter.loadPlaceholder
          : copy.body.filter.placeholder
      }
      isLoadingItems={isLoadingClasses}
      items={items}
      itemTestId={testIds.classSelectorItem}
      loadingTestId={testIds.classSelectorLoading}
      onQueryChange={(query) => {
        debouncedSetQueryKey(query);
      }}
      onSelect={handleSelect}
      popoverProps={{
        hasBackdrop: true,
        onOpening: () => {
          setWasOpened(true);
        },
      }}
      selected={subItem || item}
      subItemTestId={testIds.classSelectorSubItem}
      testId={testId}
      tooltipProps={{
        content: tooltip,
        disabled: !(tooltip && selected && invalid),
        popoverClassName: 'sd-query-builder-class-selector-tooltip',
        position: PopoverPosition.RIGHT,
      }}
    />
  );
};
